import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, Loader2, RotateCcw, X, ZoomIn } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Editor de encuadre antes de subir una imagen.
 *
 * Flujo: seleccionar imagen → mover/zoom → confirmar → devuelve un File recortado.
 * El recorte es 100% frontend: el backend sigue recibiendo el mismo base64 de siempre.
 *
 * Props:
 *  - file: File seleccionado por el usuario
 *  - aspect: proporción destino (1 = cuadrado, 4/3, 3/2, …)
 *  - onConfirm(file): recibe el archivo recortado y comprimido a JPEG
 *  - onCancel(): cierra sin tocar la selección
 *  - onSkip(file): usa la imagen original sin recortar (si allowSkip)
 */
export default function ImageCropperModal({
  file,
  aspect = 1,
  title,
  allowSkip = true,
  maxOutputSize = 1600,
  onConfirm,
  onCancel,
  onSkip,
}) {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const es = language === 'es';

  const [src, setSrc] = useState('');
  const [natural, setNatural] = useState(null);
  const [imageEl, setImageEl] = useState(null);
  const [viewWidth, setViewWidth] = useState(0);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const viewportRef = useRef(null);
  const gestureRef = useRef({ pointers: new Map(), startOffset: null, startZoom: MIN_ZOOM, startDistance: 0 });

  const viewHeight = viewWidth ? viewWidth / aspect : 0;

  // "cover": a zoom 1 la imagen ya cubre todo el marco, sin huecos.
  const baseScale = useMemo(() => {
    if (!natural || !viewWidth || !viewHeight) return 0;
    return Math.max(viewWidth / natural.w, viewHeight / natural.h);
  }, [natural, viewWidth, viewHeight]);

  const displayWidth = natural ? natural.w * baseScale * zoom : 0;
  const displayHeight = natural ? natural.h * baseScale * zoom : 0;

  const maxOffsetX = Math.max(0, (displayWidth - viewWidth) / 2);
  const maxOffsetY = Math.max(0, (displayHeight - viewHeight) / 2);

  const clampOffset = useCallback(
    (next) => ({
      x: clamp(next.x, -maxOffsetX, maxOffsetX),
      y: clamp(next.y, -maxOffsetY, maxOffsetY),
    }),
    [maxOffsetX, maxOffsetY]
  );

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });
    setError('');
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width || 0;
      if (width) setViewWidth(width);
    });
    observer.observe(node);
    setViewWidth(node.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [src]);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.0015);
      setZoom((current) => clamp(current * factor, MIN_ZOOM, MAX_ZOOM));
      setError('');
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [src]);

  // Al cambiar el zoom, el desplazamiento puede quedar fuera de rango.
  useEffect(() => {
    setOffset((current) => clampOffset(current));
  }, [clampOffset]);

  const pointerDistance = (pointers) => {
    const [a, b] = Array.from(pointers.values());
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (event) => {
    const pointers = gestureRef.current.pointers;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (pointers.size === 1) {
      gestureRef.current.startOffset = offset;
      gestureRef.current.startZoom = zoom;
      gestureRef.current.origin = null;
    } else if (pointers.size === 2) {
      gestureRef.current.startDistance = pointerDistance(pointers);
      gestureRef.current.startZoom = zoom;
    }
  };

  const onPointerMove = (event) => {
    const pointers = gestureRef.current.pointers;
    if (!pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size >= 2) {
      const distance = pointerDistance(pointers);
      if (gestureRef.current.startDistance > 0) {
        const ratio = distance / gestureRef.current.startDistance;
        setZoom(clamp(gestureRef.current.startZoom * ratio, MIN_ZOOM, MAX_ZOOM));
        setError('');
      }
      return;
    }

    const start = gestureRef.current.startOffset;
    if (!start) return;
    const [point] = Array.from(pointers.values());
    if (!gestureRef.current.origin) {
      gestureRef.current.origin = { x: point.x, y: point.y };
    }
    const origin = gestureRef.current.origin;
    setOffset(clampOffset({ x: start.x + (point.x - origin.x), y: start.y + (point.y - origin.y) }));
  };

  const endPointer = (event) => {
    const pointers = gestureRef.current.pointers;
    pointers.delete(event.pointerId);
    if (pointers.size === 0) {
      gestureRef.current.startOffset = null;
      gestureRef.current.origin = null;
      gestureRef.current.startDistance = 0;
    } else if (pointers.size === 1) {
      gestureRef.current.startOffset = offset;
      gestureRef.current.origin = null;
      gestureRef.current.startDistance = 0;
    }
  };

  const reset = () => {
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });
    setError('');
  };

  const crop = async () => {
    if (!imageEl || !natural || !baseScale) return;
    setBusy(true);
    setError('');
    try {
      const scale = baseScale * zoom;
      const cropWidth = Math.min(natural.w, viewWidth / scale);
      const cropHeight = Math.min(natural.h, viewHeight / scale);
      const sourceX = clamp(natural.w / 2 - offset.x / scale - cropWidth / 2, 0, natural.w - cropWidth);
      const sourceY = clamp(natural.h / 2 - offset.y / scale - cropHeight / 2, 0, natural.h - cropHeight);

      const outputWidth = Math.max(1, Math.min(maxOutputSize, Math.round(cropWidth)));
      const outputHeight = Math.max(1, Math.round(outputWidth / aspect));

      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, outputWidth, outputHeight);
      ctx.drawImage(imageEl, sourceX, sourceY, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) throw new Error(es ? 'No se pudo procesar la imagen.' : 'The image could not be processed.');

      const baseName = (file?.name || 'imagen').replace(/\.[^.]+$/, '');
      onConfirm?.(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }));
    } catch (e) {
      setError(e?.message || (es ? 'No se pudo recortar la imagen.' : 'The image could not be cropped.'));
      setBusy(false);
    }
  };

  const frameStyle = { width: '100%', aspectRatio: `${aspect} / 1` };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="image-cropper-modal">
      <div className="absolute inset-0 bg-black/70" onClick={() => !busy && onCancel?.()} aria-hidden="true" />
      <div className="relative flex w-full max-w-md max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <h2 className="font-heading text-base font-bold text-foreground">
            {title || (es ? 'Ajustar imagen' : 'Adjust image')}
          </h2>
          <button
            onClick={() => !busy && onCancel?.()}
            className="rounded-lg p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label={es ? 'Cerrar' : 'Close'}
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <div
            ref={viewportRef}
            style={frameStyle}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            className="relative touch-none select-none overflow-hidden rounded-xl border border-border bg-black/80"
          >
            {src && (
              <img
                src={src}
                alt=""
                draggable={false}
                ref={setImageEl}
                onLoad={(event) => {
                  const el = event.currentTarget;
                  setNatural({ w: el.naturalWidth, h: el.naturalHeight });
                }}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: displayWidth || 'auto',
                  height: displayHeight || 'auto',
                  transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
                  maxWidth: 'none',
                }}
              />
            )}
            {!natural && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/25" />
          </div>

          <p className="text-center text-xs text-muted-foreground">
            {es ? 'Arrastrá para mover · pellizcá o usá el zoom para acercar' : 'Drag to move · pinch or use zoom to get closer'}
          </p>

          <div className="flex items-center gap-2">
            <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
              className="h-1.5 w-full accent-amber-500"
              aria-label={es ? 'Zoom' : 'Zoom'}
            />
            <button
              type="button"
              onClick={reset}
              className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              aria-label={es ? 'Restablecer' : 'Reset'}
              title={es ? 'Restablecer' : 'Reset'}
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>

          {error && <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        </div>

        <div className="flex gap-2 border-t border-border px-5 py-4">
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            className="flex-1 rounded-lg border border-border bg-surface py-2.5 text-sm font-semibold text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            {es ? 'Cancelar' : 'Cancel'}
          </button>
          {allowSkip && (
            <button
              type="button"
              onClick={() => !busy && (onSkip ? onSkip(file) : onConfirm?.(file))}
              disabled={busy}
              className="flex-1 rounded-lg border border-border bg-surface py-2.5 text-sm font-semibold text-muted-foreground transition hover:bg-muted disabled:opacity-50"
              data-testid="image-cropper-skip"
            >
              {es ? 'Omitir' : 'Skip'}
            </button>
          )}
          <button
            type="button"
            onClick={crop}
            disabled={busy || !natural}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition hover:opacity-90 disabled:opacity-50"
            style={theme === 'light' ? { backgroundColor: '#111111', color: '#ffffff' } : { backgroundColor: '#ffffff', color: '#111111' }}
            data-testid="image-cropper-confirm"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {es ? 'Confirmar' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
